// src/components/screener/SavedScansPanel.jsx
// Extracted from ScreenerPage.jsx (SCREENER-FRAG). JSX content unchanged —
// state/handlers moved into useSavedScans.js, this file is render-only.
//
// Props:
//   isLoggedIn        bool
//   onRequestInvite   () => void — guest CTA
//   currentFilters    { signal, sector, sort, scanUsed } — what "Save Scan" saves
//   onLoadScan        (scan) => void — parent applies scan.filters + fetchResults
//                     + sets savedScanUsedRef (D3 strategy_declaration signal)
//   onAlertsCountChange (count) => void — parent tracks alerts.length for the
//                     D3 signal-log payload (alertsActive)

import React from 'react';
import { S } from '../../constants/screenerConfig';
import { useSavedScans } from '../../hooks/useSavedScans';

export default function SavedScansPanel({ isLoggedIn, onRequestInvite, currentFilters, onLoadScan, onAlertsCountChange }) {
  const {
    savedScans, alerts, showLibrary, setShowLibrary,
    showSaveInput, setShowSaveInput, saveName, setSaveName,
    saveLoading, saveError, setSaveError, alertLoading, scanToDelete, setScanToDelete,
    handleSaveScan, handleLoadScan, handleToggleAlert, handleDeleteScan,
  } = useSavedScans({ isLoggedIn, onLoadScan, onAlertsCountChange });

  return (
    <>
      {/* Save scan button (member) or gate (guest) */}
      {isLoggedIn ? (
        <button
          onClick={() => { setShowSaveInput(v => !v); setSaveError(''); }}
          style={{
            padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700,
            background: showSaveInput ? 'rgba(99,102,241,0.15)' : 'transparent',
            color: S.purple, border: `1px solid ${S.purple}`,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          💾 Save Scan
        </button>
      ) : (
        <button
          onClick={onRequestInvite}
          style={{
            padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700,
            background: 'transparent', color: S.purple, border: `1px solid ${S.purple}`,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          💾 Save Scan →
        </button>
      )}

      {/* ── Save scan inline input ── */}
      {isLoggedIn && showSaveInput && (
        <div style={{
          padding: '12px 14px', background: 'rgba(99,102,241,0.06)',
          border: `1px solid rgba(99,102,241,0.25)`, borderRadius: 10, marginBottom: 12,
          display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <input
            value={saveName}
            onChange={e => setSaveName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSaveScan(currentFilters); if (e.key === 'Escape') setShowSaveInput(false); }}
            placeholder='Name this scan — e.g. "My Oversold Tech Plays"'
            maxLength={60}
            autoFocus
            style={{
              flex: 1, minWidth: 200, padding: '8px 12px', borderRadius: 8, fontSize: 13,
              background: S.surface, color: S.text, border: `1px solid ${S.border2}`,
              fontFamily: 'inherit', outline: 'none',
            }}
          />
          <button
            onClick={() => handleSaveScan(currentFilters)}
            disabled={saveLoading || !saveName.trim()}
            style={{
              padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700,
              background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff',
              border: 'none', cursor: saveLoading ? 'wait' : 'pointer', fontFamily: 'inherit',
              opacity: !saveName.trim() ? 0.5 : 1,
            }}
          >
            {saveLoading ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={() => { setShowSaveInput(false); setSaveError(''); }}
            style={{
              padding: '8px 12px', borderRadius: 8, fontSize: 13,
              background: 'transparent', color: S.muted, border: `1px solid ${S.border2}`,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          {saveError && <span style={{ fontSize: 12, color: S.red, width: '100%' }}>{saveError}</span>}
          <span style={{ fontSize: 11, color: S.subtle, width: '100%' }}>
            Saving: Signal={currentFilters.signal} · Sector={currentFilters.sector} · Sort={currentFilters.sort}{currentFilters.scanUsed ? ` · Scan=${currentFilters.scanUsed}` : ''}
          </span>
        </div>
      )}

      {/* ── Saved scan library ── */}
      {isLoggedIn && savedScans.length > 0 && (
        <div style={{
          background: S.surface, border: `1px solid ${S.border2}`, borderRadius: 10,
          marginBottom: 16, overflow: 'hidden',
        }}>
          <button
            onClick={() => setShowLibrary(v => !v)}
            style={{
              width: '100%', padding: '10px 14px', background: 'transparent', border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 700, color: S.text }}>
              📁 Saved Scans
              <span style={{
                marginLeft: 8, fontSize: 11, padding: '1px 7px', borderRadius: 10,
                background: 'rgba(99,102,241,0.15)', color: S.purple,
              }}>
                {savedScans.length}
              </span>
            </span>
            <span style={{ fontSize: 12, color: S.muted }}>{showLibrary ? '▲' : '▼'}</span>
          </button>

          {showLibrary && (
            <div style={{ padding: '0 0 8px 0' }}>
              {savedScans.map(scan => {
                const hasAlert    = alerts.some(a => String(a.savedScanId) === String(scan._id));
                const alertBusy   = alertLoading === scan._id;
                const confirmDel  = scanToDelete === scan._id;
                return (
                  <div
                    key={scan._id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                      padding: '8px 14px', borderTop: `1px solid ${S.border}`,
                    }}
                  >
                    {/* Load button */}
                    <button
                      onClick={() => handleLoadScan(scan)}
                      style={{
                        flex: 1, minWidth: 140, textAlign: 'left',
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        padding: 0, fontFamily: 'inherit',
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 700, color: S.text }}>{scan.name}</span>
                      <span style={{ marginLeft: 8, fontSize: 11, color: S.muted }}>
                        {[
                          scan.filters.scanUsed ? `Scan: ${scan.filters.scanUsed}` : null,
                          scan.filters.signal !== 'ALL' ? scan.filters.signal : null,
                          scan.filters.sector !== 'ALL' ? scan.filters.sector : null,
                        ].filter(Boolean).join(' · ') || 'All signals'}
                      </span>
                    </button>

                    {/* D3 strategy badge */}
                    {scan.d3Strategy && (
                      <span style={{
                        fontSize: 10, padding: '2px 8px', borderRadius: 6, fontWeight: 700,
                        background: 'rgba(99,102,241,0.1)', color: S.purple, letterSpacing: '0.05em',
                      }}>
                        {scan.d3Strategy}
                      </span>
                    )}

                    {/* Alert toggle */}
                    <button
                      onClick={() => handleToggleAlert(scan)}
                      disabled={alertBusy}
                      title={hasAlert ? 'Alert active — click to remove' : 'Set alert for this scan'}
                      style={{
                        padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                        cursor: alertBusy ? 'wait' : 'pointer', fontFamily: 'inherit',
                        border: `1px solid ${hasAlert ? S.amber : S.border2}`,
                        background: hasAlert ? 'rgba(245,158,11,0.1)' : 'transparent',
                        color: hasAlert ? S.amber : S.muted,
                        transition: 'all 0.15s',
                      }}
                    >
                      {alertBusy ? '…' : hasAlert ? '🔔 Alerting' : '🔕 Alert'}
                    </button>

                    {/* Delete */}
                    {!confirmDel ? (
                      <button
                        onClick={() => setScanToDelete(scan._id)}
                        title="Remove saved scan"
                        style={{
                          padding: '4px 8px', borderRadius: 6, fontSize: 11,
                          background: 'transparent', color: S.subtle,
                          border: `1px solid ${S.border}`, cursor: 'pointer', fontFamily: 'inherit',
                        }}
                      >
                        ✕
                      </button>
                    ) : (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: S.red }}>Remove?</span>
                        <button
                          onClick={() => handleDeleteScan(scan)}
                          style={{
                            padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                            background: S.red, color: '#fff', border: 'none',
                            cursor: 'pointer', fontFamily: 'inherit',
                          }}
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setScanToDelete(null)}
                          style={{
                            padding: '3px 8px', borderRadius: 6, fontSize: 11,
                            background: 'transparent', color: S.muted,
                            border: `1px solid ${S.border2}`, cursor: 'pointer', fontFamily: 'inherit',
                          }}
                        >
                          No
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {alerts.length > 0 && (
                <div style={{ padding: '8px 14px', borderTop: `1px solid ${S.border}`, fontSize: 11, color: S.muted }}>
                  🔔 {alerts.length} active alert{alerts.length !== 1 ? 's' : ''} — emails sent via SMTP2GO during market hours
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
